import { useLanguage } from "@/context/LanguageContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useTodayInIndia } from "@/lib/useTodayInIndia";
import { useState, useId, useRef, cloneElement } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Loader2, CheckCircle2, AlertTriangle, ChevronDown } from "lucide-react";
import {
  SITE,
  telHref,
  waHref,
  buildTripMessage,
  VEHICLE_CATEGORIES,
  VEHICLE_PREFERENCES,
  TRIP_TYPES,
} from "@/config/site";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { todayInIndia, isValidPhone, isValidPassengers } from "@/lib/enquiryValidation";
import { trackGenerateLead } from "@/lib/analytics";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

const empty = {
  name: "",
  callback_phone: "",
  trip_type: "",
  pickup_city: "",
  pickup_point: "",
  destination: "",
  travel_date: "",
  travel_time: "",
  return_date: "",
  passengers: "",
  luggage_note: "",
  vehicle_category: "",
  vehicle_preference: "",
  message: "",
};

const Field = ({ label, htmlFor, required, children, error }) => {
  const { t } = useLanguage();
  return (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={htmlFor} className="text-sm font-semibold text-slate-700">
      {t(label)} {required && <span className="text-amber-600">*</span>}
    </Label>
    {children.type === Input || children.type === Textarea ? cloneElement(children, {
      required: Boolean(required), "aria-invalid": Boolean(error),
      "aria-describedby": error ? `${htmlFor}-error` : undefined,
    }) : children}
    {error && <span id={`${htmlFor}-error`} className="text-xs font-medium text-red-600">{t(error)}</span>}
  </div>
  );
};

// Localize only the readable message. Form values and API payloads stay unchanged.
const buildHindiTripMessage = (trip, t) => {
  const lines = [t("Namaste Jitendra ji, I would like to request a taxi / Tempo Traveller quote."), ""];
  const fields = [
    ["Name", "name"], ["Trip type", "trip_type"], ["Pickup city", "pickup_city"],
    ["Pickup point", "pickup_point"], ["Destination", "destination"],
    ["Travel date", "travel_date"], ["Travel time", "travel_time"],
    ["Return date", "return_date"], ["Passengers", "passengers"],
    ["Luggage", "luggage_note"], ["Vehicle", "vehicle_category"],
    ["Vehicle preference", "vehicle_preference"], ["Notes", "message"],
    ["Callback number", "callback_phone"],
  ];
  const choiceFields = new Set(["trip_type", "vehicle_category", "vehicle_preference"]);
  for (const [label, key] of fields) {
    const value = trip[key];
    if (value && String(value).trim()) {
      const displayValue = choiceFields.has(key) ? t(String(value).trim()) : String(value).trim();
      lines.push(`${t(label)}: ${displayValue}`);
    }
  }
  lines.push("", t("Please confirm availability and fare. Thank you."));
  return lines.join("\n");
};

export const TripEnquiryForm = ({ prefill = {}, sourcePage = "", onSuccess }) => {
  const { t, locale } = useLanguage();
  const formId = useId();
  const idFor = (name) => `${formId}-${name}`;
  const today = useTodayInIndia();
  const [form, setForm] = useState({ ...empty, ...prefill });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const submittingRef = useRef(false);
  const [showOptional, setShowOptional] = useState(
    Boolean(prefill.pickup_point || prefill.return_date || prefill.trip_type || prefill.message),
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setVal = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const validate = () => {
    const e = {};
    if (!form.pickup_city.trim()) e.pickup_city = "Pickup city is required";
    if (!form.destination.trim()) e.destination = "Destination is required";
    if (!form.travel_date) e.travel_date = "Travel date is required";
    else if (form.travel_date < todayInIndia()) e.travel_date = "Travel date can't be in the past";
    if (!isValidPhone(form.callback_phone))
      e.callback_phone = "Enter a valid phone number (10 to 15 digits, with country code if needed)";
    if (!isValidPassengers(form.passengers)) e.passengers = "Enter a whole number from 1 to 999";
    if (form.return_date && form.travel_date && form.return_date < form.travel_date)
      e.return_date = "Return date must be on or after the travel date";
    if (e.return_date) setShowOptional(true);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const waText = locale === "hi"
    ? buildHindiTripMessage(form, t)
    : buildTripMessage({ ...form, source_page: sourcePage });

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!validate()) {
      toast.error(t("Please correct the highlighted fields."));
      return;
    }
    submittingRef.current = true;
    setStatus("submitting");
    try {
      const response = await axios.post(`${API}/enquiries`, { ...form, source_page: sourcePage });
      if (response.data?.status !== "success") throw new Error("The server did not confirm the enquiry.");
      trackGenerateLead(sourcePage === "contact-page" ? "contact" : "modal");
      setStatus("success");
      toast.success(t("Enquiry sent — we'll call you back shortly."));
      onSuccess?.();
    } catch (err) {
      setStatus("error");
      toast.error(t("Could not send enquiry. Please retry or use WhatsApp / Call."));
    } finally {
      submittingRef.current = false;
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-center" data-testid="enquiry-success" role="status" aria-live="polite">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" aria-hidden="true" />
        <h3 className="mt-3 text-xl font-bold text-slate-900">{t("Enquiry received")}</h3>
        <p className="mt-2 text-sm text-slate-600">
          {t("Thank you{nameSuffix}. Your enquiry has been received and we'll call you back on {callbackPhone} with a quote. For a faster reply, message us on WhatsApp too.", { nameSuffix: form.name ? `, ${form.name}` : "", callbackPhone: form.callback_phone })}
        </p>
        <div className="mt-4 flex flex-col sm:flex-row justify-center gap-3">
          <a
            href={waHref(SITE.phonePrimary.wa, waText)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enquiry-success-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 font-semibold text-white hover:bg-emerald-700"
          >
            <WhatsAppIcon className="h-4 w-4" /> {t("Also send on WhatsApp")}
          </a>
          <button
            type="button"
            onClick={() => {
              setForm({ ...empty });
              setStatus("idle");
            }}
            className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="enquiry-new-button"
          >
            {t("New enquiry")}
          </button>
        </div>
      </div>
    );
  }

  const vehicleCategoryOptions =
    form.vehicle_category && !VEHICLE_CATEGORIES.includes(form.vehicle_category)
      ? [form.vehicle_category, ...VEHICLE_CATEGORIES]
      : VEHICLE_CATEGORIES;

  const basePref = VEHICLE_PREFERENCES[form.vehicle_category] || [];
  const prefOptions =
    form.vehicle_preference && !basePref.includes(form.vehicle_preference)
      ? [form.vehicle_preference, ...basePref]
      : basePref;
  const isTempo = form.vehicle_category === "Tempo Traveller";
  const prefLabel = isTempo
    ? "Seating"
    : "Vehicle preference (optional)";
  const prefPlaceholder = isTempo ? "Select seating" : "No preference";

  const onCategoryChange = (val) =>
    setForm((f) => ({ ...f, vehicle_category: val, vehicle_preference: "" }));

  return (
    <form onSubmit={submit} data-testid="trip-enquiry-form" className="space-y-4" noValidate>
      {Object.keys(errors).length > 0 && <p role="alert" className="text-sm font-semibold text-red-700">{t("Please correct the highlighted fields.")}</p>}
      {/* Essential trip details */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Pickup city" htmlFor={idFor("pickup_city")} required error={errors.pickup_city}>
          <Input id={idFor("pickup_city")} maxLength={160} data-testid="enquiry-pickup-city-input" value={form.pickup_city} onChange={set("pickup_city")} placeholder={t("e.g. Satna")} />
        </Field>
        <Field label="Destination / itinerary" htmlFor={idFor("destination")} required error={errors.destination}>
          <Input id={idFor("destination")} maxLength={1000} data-testid="enquiry-destination-input" value={form.destination} onChange={set("destination")} placeholder={t("e.g. Maihar temple")} />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Travel date" htmlFor={idFor("travel_date")} required error={errors.travel_date}>
          <Input id={idFor("travel_date")} type="date" min={today} data-testid="enquiry-travel-date-input" value={form.travel_date} onChange={set("travel_date")} />
        </Field>
        <Field label="Passengers" htmlFor={idFor("passengers")} error={errors.passengers}>
          <Input id={idFor("passengers")} type="number" min="1" max="999" step="1" data-testid="enquiry-passengers-input" value={form.passengers} onChange={set("passengers")} placeholder={t("e.g. 4")} />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Vehicle type" htmlFor={idFor("vehicle_category")}>
          <Select value={form.vehicle_category} onValueChange={onCategoryChange}>
            <SelectTrigger id={idFor("vehicle_category")} data-testid="enquiry-vehicle-category-select">
              <SelectValue placeholder={t("Select vehicle type")} />
            </SelectTrigger>
            <SelectContent>
              {vehicleCategoryOptions.map((v) => (
                <SelectItem key={v} value={v}>{t(v)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Callback number" htmlFor={idFor("callback_phone")} required error={errors.callback_phone}>
          <Input id={idFor("callback_phone")} type="tel" autoComplete="tel" maxLength={32} data-testid="enquiry-callback-phone-input" value={form.callback_phone} onChange={set("callback_phone")} placeholder={t("e.g. 98765 43210")} />
        </Field>
      </div>

      {prefOptions.length > 0 && (
        <Field label={prefLabel} htmlFor={idFor("vehicle_preference")}>
          <Select value={form.vehicle_preference} onValueChange={setVal("vehicle_preference")}>
            <SelectTrigger id={idFor("vehicle_preference")} data-testid="enquiry-vehicle-preference-select">
              <SelectValue placeholder={t(prefPlaceholder)} />
            </SelectTrigger>
            <SelectContent>
              {prefOptions.map((v) => (
                <SelectItem key={v} value={v}>{t(v)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      {/* Optional details (collapsible) */}
      <button
        type="button"
        onClick={() => setShowOptional((v) => !v)}
        data-testid="enquiry-toggle-optional"
        className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-amber-700"
        aria-expanded={showOptional}
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${showOptional ? "rotate-180" : ""}`} aria-hidden="true" />
        {t(showOptional ? "Hide optional details" : "Add trip details (optional)")}
      </button>

      {showOptional && (
        <div className="space-y-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="enquiry-optional-section">
          <Field label="Exact pickup point" htmlFor={idFor("pickup_point")}>
            <Input id={idFor("pickup_point")} data-testid="enquiry-pickup-point-input" value={form.pickup_point} onChange={set("pickup_point")} placeholder={t("e.g. Satna railway station / home address")} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Time" htmlFor={idFor("travel_time")}>
              <Input id={idFor("travel_time")} type="time" data-testid="enquiry-travel-time-input" value={form.travel_time} onChange={set("travel_time")} />
            </Field>
            <Field label="Return date" htmlFor={idFor("return_date")} error={errors.return_date}>
              <Input id={idFor("return_date")} type="date" min={form.travel_date || today} data-testid="enquiry-return-date-input" value={form.return_date} onChange={set("return_date")} />
            </Field>
            <Field label="Trip type" htmlFor={idFor("trip_type")}>
              <Select value={form.trip_type} onValueChange={setVal("trip_type")}>
                <SelectTrigger id={idFor("trip_type")} data-testid="enquiry-trip-type-select">
                  <SelectValue placeholder={t("Select")} />
                </SelectTrigger>
                <SelectContent>
                  {TRIP_TYPES.map((tripType) => (
                    <SelectItem key={tripType} value={tripType}>{t(tripType)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Luggage note" htmlFor={idFor("luggage_note")}>
              <Input id={idFor("luggage_note")} data-testid="enquiry-luggage-input" value={form.luggage_note} onChange={set("luggage_note")} placeholder={t("e.g. 4 bags")} />
            </Field>
            <Field label="Your name" htmlFor={idFor("name")}>
              <Input id={idFor("name")} autoComplete="name" maxLength={2000} data-testid="enquiry-name-input" value={form.name} onChange={set("name")} placeholder={t("Name")} />
            </Field>
          </div>
          <Field label="Notes" htmlFor={idFor("message")}>
            <Textarea id={idFor("message")} maxLength={2000} data-testid="enquiry-message-input" value={form.message} onChange={set("message")} placeholder={t("Anything else we should know about your trip?")} rows={2} />
          </Field>
        </div>
      )}

      {status === "error" && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="enquiry-error" role="alert">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{t('We couldn\'t send your enquiry just now. Please tap "Retry", or reach us directly on WhatsApp or Call below.')}</span>
        </div>
      )}

      <div className="flex flex-col gap-3 pt-1">
        <button
          type="submit"
          disabled={status === "submitting"}
          data-testid="enquiry-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-base font-bold text-slate-950 shadow-lg transition-colors hover:bg-amber-400 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" /> {t("Sending…")}
            </>
          ) : status === "error" ? (
            t("Retry sending enquiry")
          ) : (
            t("Request a Quote")
          )}
        </button>

        <div className="grid grid-cols-2 gap-3">
          <a
            href={waHref(SITE.phonePrimary.wa, waText)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enquiry-whatsapp-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 font-semibold text-white hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
          >
            <WhatsAppIcon className="h-4 w-4" /> {t("Send on WhatsApp")}
          </a>
          <a
            href={telHref(SITE.phonePrimary.tel)}
            data-testid="enquiry-call-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 px-4 py-2.5 font-semibold text-white hover:bg-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
          >
            <Phone className="h-4 w-4" /> {t("Call Now")}
          </a>
        </div>
        <p className="text-center text-xs text-slate-500">
          {t("Tapping WhatsApp opens a prefilled message for you to send — it doesn't confirm a booking. Use the published tariffs as a guide; we confirm availability and the final quotation for your itinerary.")}
        </p>
      </div>
    </form>
  );
};
